/**
 * NOS-198: shared confirmation for the import preview gate's "Stop here" action.
 * Both surfaces that render the gate — the builder ImportReviewPanel and the
 * onboarding wizard — wrap their "Stop here" button with this dialog so a user
 * cannot finalize a sample-only import without seeing the real discard counts.
 *
 * The gate's continue/stop contract is unchanged; this only interposes an
 * AlertDialog before the stop action runs.
 */
import { useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import type { ImportRemainingSummary } from "@/hooks/useImportJob"

export interface StopImportConfirmDialogProps {
  /** Real remaining counts from job state — drives the discard message. */
  remaining: ImportRemainingSummary | null | undefined
  /** Runs when the user confirms discarding the rest of the import. */
  onConfirm: () => void | Promise<void>
  /** The "Stop here" button, styled by the host surface, used as the trigger. */
  trigger: React.ReactNode
  /** Disables the trigger (e.g. while another gate action is in flight). */
  disabled?: boolean
}

/** "the remaining 42 pages and 340 posts will NOT be imported" (real counts). */
export function discardMessage(
  remaining: ImportRemainingSummary | null | undefined
): string {
  const parts: string[] = []
  if (remaining && remaining.pages > 0) {
    parts.push(`${remaining.pages} page${remaining.pages !== 1 ? "s" : ""}`)
  }
  if (remaining && remaining.posts > 0) {
    parts.push(`${remaining.posts} post${remaining.posts !== 1 ? "s" : ""}`)
  }
  if (parts.length === 0) {
    return "This finalizes your import with only the sample you previewed. You can’t undo this from here."
  }
  return `This finalizes your import with only the sample you previewed — the remaining ${parts.join(
    " and "
  )} will NOT be imported. You can’t undo this from here.`
}

export function StopImportConfirmDialog({
  remaining,
  onConfirm,
  trigger,
  disabled = false,
}: StopImportConfirmDialogProps) {
  const [open, setOpen] = useState(false)
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild disabled={disabled}>
        {trigger}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Import only the sample?</AlertDialogTitle>
          <AlertDialogDescription>{discardMessage(remaining)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep reviewing</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              void onConfirm()
            }}
          >
            Discard remaining pages
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
